import React, { useEffect, useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import { formatUsd } from "./paymentFeature";
import { listPaidBadgeSets } from "./paymentsService";

const getBadgeSetTable = (payment) => {
  const spreadsheetData = payment.spreadsheetData || {};
  const headers = spreadsheetData.fileData?.headers || [];
  const rows = spreadsheetData.fileData?.data || [];
  const mapping = payment.fieldMapping || spreadsheetData.mapping || {};
  const columns = Object.entries(mapping)
    .map(([header, fieldName]) => ({
      fieldName,
      index: headers.indexOf(header),
    }))
    .filter(({ index, fieldName }) => index !== -1 && fieldName);

  return { columns, rows };
};

const PurchasedBadgesDialog = ({
  eventId,
  onClose,
  onRegenerate,
  open,
  userId,
}) => {
  const [badgeSets, setBadgeSets] = useState([]);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let isStale = false;
    if (!open) return undefined;

    setIsLoading(true);
    setError("");
    listPaidBadgeSets(eventId, userId)
      .then((payments) => {
        if (!isStale) setBadgeSets(payments);
      })
      .catch((loadError) => {
        if (!isStale) {
          setError(loadError.message || "Could not load purchased badges.");
        }
      })
      .finally(() => {
        if (!isStale) setIsLoading(false);
      });

    return () => {
      isStale = true;
    };
  }, [eventId, open, userId]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth disableEnforceFocus>
      <DialogContent className="purchased-badges-dialog-content">
        <h3 className="dialog-title">Purchased badges</h3>
        {isLoading && <div className="purchased-badges-status">Loading purchased sets...</div>}
        {error && <div className="purchased-badges-error" role="alert">{error}</div>}
        {!isLoading && !error && badgeSets.length === 0 && (
          <div className="purchased-badges-status">No purchased badge sets yet.</div>
        )}
        {!isLoading && !error && badgeSets.map((payment, setIndex) => {
          const { columns, rows } = getBadgeSetTable(payment);
          const purchaseDate = new Date(payment.paidAt || payment.createdAt);

          return (
            <section className="purchased-badge-set" key={payment.id}>
              <div className="purchased-badge-set-header">
                <div>
                  <strong>Badge set {badgeSets.length - setIndex}</strong>
                  <div className="purchased-badge-set-meta">
                    {payment.attendeeCount} attendee{payment.attendeeCount === 1 ? "" : "s"}
                    {" · "}{formatUsd(payment.amountCents)} paid
                    {payment.badgeCreditPurchasedCount > 0 && (
                      <>{" · "}{payment.badgeCreditPurchasedCount} badge credit purchased</>
                    )}
                    {payment.badgeCreditUsedCount > 0 && (
                      <>{" · "}{payment.badgeCreditUsedCount} badge credit used</>
                    )}
                    {" · "}{purchaseDate.toLocaleString()}
                  </div>
                </div>
                <button
                  className="primary"
                  type="button"
                  onClick={() => onRegenerate(payment)}
                >
                  Regenerate badges
                </button>
              </div>
              <div className="purchased-badge-table-scroll">
                <table className="purchased-badge-table">
                  <thead>
                    <tr>
                      {columns.map(({ fieldName }, index) => (
                        <th key={`${fieldName}-${index}`}>{fieldName}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, rowIndex) => (
                      <tr key={rowIndex}>
                        {columns.map(({ fieldName, index }, columnIndex) => (
                          <td key={`${fieldName}-${columnIndex}`}>{String(row[index] ?? "")}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          );
        })}
      </DialogContent>
      <DialogActions>
        <button className="button" onClick={onClose}>Close</button>
      </DialogActions>
    </Dialog>
  );
};

export default PurchasedBadgesDialog;
